import type { Id } from "@supermanus/document-model";

const CAMERA_OPTIONS = Array.from({ length: 24 }, (_, i) => i + 1);

export type CueSetupMetaPatch = {
  cameraNumber?: number | null;
  cameraNote?: string;
  positionNote?: string;
};

export interface CameraNoteLineProps {
  cueId: Id;
  cameraNumber?: number;
  cameraNote?: string;
  readOnly?: boolean;
  onChange: (cueId: Id, patch: CueSetupMetaPatch) => void;
}

/**
 * KAM N selector + optional framing/movement note on the cue meta row.
 */
export function CameraNoteLine({
  cueId,
  cameraNumber,
  cameraNote,
  readOnly,
  onChange,
}: CameraNoteLineProps) {
  const hasCamera = cameraNumber != null && cameraNumber >= 1;
  const number = hasCamera ? cameraNumber : undefined;
  const note = cameraNote ?? "";

  if (readOnly) {
    const kamLabel = hasCamera ? `KAM ${number}` : "";
    const text = [kamLabel, note.trim()].filter(Boolean).join("  ");
    if (!text) return null;
    return (
      <span className="sm-camera-note sm-camera-note-readonly" title={text}>
        {hasCamera ? (
          <span className="sm-camera-note-kam">KAM {number}</span>
        ) : null}
        {note.trim() ? (
          <span className="sm-camera-note-text">{note.trim()}</span>
        ) : null}
      </span>
    );
  }

  return (
    <div
      className="sm-camera-note"
      onClick={(e) => e.stopPropagation()}
      onPointerDown={(e) => e.stopPropagation()}
    >
      <label className="sm-camera-note-kam">
        <span className="sm-sr-only">Kamera</span>
        <select
          value={number ?? ""}
          aria-label="Kameranummer"
          className={hasCamera ? undefined : "is-empty"}
          onChange={(e) => {
            const raw = e.target.value;
            onChange(cueId, {
              cameraNumber: raw === "" ? null : Number(raw),
            });
          }}
        >
          <option value="">KAM #</option>
          {CAMERA_OPTIONS.map((n) => (
            <option key={n} value={n}>
              KAM {n}
            </option>
          ))}
        </select>
      </label>
      <input
        type="text"
        className="sm-camera-note-input"
        value={note}
        size={Math.max(1, note.length)}
        placeholder="Beskæring, bevægelse, zoom…"
        aria-label="Kameranote"
        onChange={(e) => onChange(cueId, { cameraNote: e.target.value })}
        onBlur={(e) => {
          const trimmed = e.target.value.trimEnd();
          if (trimmed !== e.target.value) {
            onChange(cueId, { cameraNote: trimmed });
          }
        }}
        onKeyDown={(e) => e.stopPropagation()}
      />
    </div>
  );
}

/** Invisible stand-in so paired cells keep equal meta-row height. */
export function CameraNoteSpacer({
  cameraNumber,
  cameraNote,
}: {
  cameraNumber?: number;
  cameraNote?: string;
}) {
  const hasCamera = cameraNumber != null && cameraNumber >= 1;
  const note = cameraNote?.trim() ?? "";
  return (
    <span className="sm-camera-note sm-camera-note-spacer" aria-hidden>
      <span className="sm-camera-note-kam">
        {hasCamera ? `KAM ${cameraNumber}` : "KAM #"}
      </span>
      {note ? <span className="sm-camera-note-text">{note}</span> : null}
    </span>
  );
}

export interface PositionNoteLineProps {
  cueId: Id;
  positionNote?: string;
  readOnly?: boolean;
  onChange: (cueId: Id, patch: CueSetupMetaPatch) => void;
}

/** Free-text position note shown in display column 2. */
export function PositionNoteLine({
  cueId,
  positionNote,
  readOnly,
  onChange,
}: PositionNoteLineProps) {
  const note = positionNote ?? "";

  if (readOnly) {
    const text = note.trim();
    if (!text) return null;
    return (
      <span className="sm-position-note sm-position-note-readonly" title={text}>
        <span className="sm-position-note-label">Pos.</span>
        <span className="sm-position-note-text">{text}</span>
      </span>
    );
  }

  return (
    <div
      className="sm-position-note"
      onClick={(e) => e.stopPropagation()}
      onPointerDown={(e) => e.stopPropagation()}
    >
      <span className="sm-position-note-label" aria-hidden>
        Pos.
      </span>
      <input
        type="text"
        className="sm-position-note-input"
        value={note}
        size={Math.max(1, note.length)}
        placeholder="Position…"
        aria-label="Position"
        onChange={(e) => onChange(cueId, { positionNote: e.target.value })}
        onBlur={(e) => {
          const trimmed = e.target.value.trimEnd();
          if (trimmed !== e.target.value) {
            onChange(cueId, { positionNote: trimmed });
          }
        }}
        onKeyDown={(e) => e.stopPropagation()}
      />
    </div>
  );
}

/** Invisible stand-in matching PositionNoteLine height. */
export function PositionNoteSpacer({ positionNote }: { positionNote?: string }) {
  const note = positionNote?.trim() ?? "";
  return (
    <span className="sm-position-note sm-position-note-spacer" aria-hidden>
      <span className="sm-position-note-label">Pos.</span>
      {note ? <span className="sm-position-note-text">{note}</span> : null}
    </span>
  );
}
